/* @img-migrated */
import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Instagram, Linkedin, Youtube, ExternalLink, LifeBuoy } from 'lucide-react';

import Img from '../Img';
const SOCIAL_LINKS = [
  { icon: Instagram, label: 'Instagram', href: 'https://www.instagram.com/dominicanchamberfl/' },
  { icon: Linkedin, label: 'LinkedIn', href: 'https://www.linkedin.com/in/dominican-chamber-of-commerce-of-florida-dccf-b4a736376' },
  { icon: Facebook, label: 'Facebook', href: 'https://www.facebook.com/p/Dominican-Chamber-of-Florida-61576763651371/' },
  { icon: Youtube, label: 'YouTube', href: 'https://www.youtube.com/@DominicanChamberOfFlorida' },
];

const Footer = () => {
  const quickLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Membership', path: '/membership' },
    { name: 'Events', path: '/events' },
    { name: 'News & Insights', path: '/news' },
    { name: 'Leadership', path: '/leadership' },
    { name: 'Contact', path: '/contact' },
  ];

  const resourceLinks = [
    { name: 'Members Directory', path: '/members' },
    { name: 'Brand Resources', path: '/brand-resources' },
    { name: 'Marketing Request', path: '/marketing-request' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Member Portal', path: '/member-login' },
  ];

  return (
    <footer className="bg-[#002249] text-white relative" data-testid="footer">
      {/* Red accent bar — clean brand stripe (no gradient with old navy tones) */}
      <div className="h-1 bg-[#E63946]" />
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 pt-14 sm:pt-16 pb-10 sm:pb-12">
        {/* Top: Logo + Social */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pb-10 border-b border-white/10">
          <Link to="/" className="block" data-testid="footer-logo">
            <Img variant="generic"
              src="/brand/logo-white.png"
              alt="DCCF Logo"
              className="h-14 sm:h-16 w-auto object-contain"
            />
          </Link>
          <div className="flex items-center gap-3">
            {SOCIAL_LINKS.map(s => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/8 hover:bg-white/15 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:scale-110 transition-all duration-200"
                aria-label={s.label}
                data-testid={`social-${s.label.toLowerCase()}`}
              >
                <s.icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        {/* Grid: Links + Contact */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12 pt-10">
          {/* About column */}
          <div className="sm:col-span-2 lg:col-span-1">
            <p className="text-white/60 text-sm leading-relaxed max-w-xs">
              The Dominican Chamber of Commerce of Florida connects, empowers, and advocates for Dominican entrepreneurs and professionals throughout the state.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-[13px] font-semibold text-white/40 uppercase tracking-widest mb-5">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/60 hover:text-white text-sm transition-colors duration-200"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-[13px] font-semibold text-white/40 uppercase tracking-widest mb-5">Resources</h4>
            <ul className="space-y-3">
              {resourceLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/60 hover:text-white text-sm transition-colors duration-200"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-[13px] font-semibold text-white/40 uppercase tracking-widest mb-5">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#E63946] mt-0.5 flex-shrink-0" />
                <span className="text-white/60 text-sm leading-relaxed">
                  28 Broadway Ave, Suite 209<br />
                  Kissimmee, FL 34741
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#E63946] flex-shrink-0" />
                <a href="tel:1-866-310-3380" className="text-white/60 hover:text-white text-sm transition-colors duration-200">
                  1-866-310-3380
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#E63946] flex-shrink-0" />
                <a href="mailto:info@dominicanchamberfl.org" className="text-white/60 hover:text-white text-sm transition-colors duration-200">
                  info@dominicanchamberfl.org
                </a>
              </li>
              <li className="flex items-start gap-3 pt-2 border-t border-white/10 mt-1">
                <LifeBuoy className="w-4 h-4 text-[#E63946] flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-white text-[12.5px] font-bold leading-tight">Membership Relations</p>
                  <a
                    href="mailto:membership@dominicanchamberfl.org"
                    className="text-white/60 hover:text-white text-[12px] transition-colors duration-200 break-all"
                    data-testid="footer-membership-relations"
                  >
                    membership@dominicanchamberfl.org
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
            <p className="text-white/35 text-[13px] text-center sm:text-left">
              The Dominican Chamber of Commerce website is powered by{' '}
              <a
                href="https://eternitymp.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/50 hover:text-white/70 transition-colors duration-200 underline-offset-2 hover:underline"
                data-testid="footer-eternity-link"
              >
                Eternity MP
              </a>
            </p>
            <div className="flex items-center gap-4">
              <Link
                to="/privacy"
                className="text-white/50 hover:text-white/85 text-[12.5px] font-medium transition-colors duration-200"
                data-testid="footer-privacy-link"
              >
                Privacy Policy
              </Link>
              <span className="text-white/15">·</span>
              <Link
                to="/terms"
                className="text-white/50 hover:text-white/85 text-[12.5px] font-medium transition-colors duration-200"
                data-testid="footer-terms-link"
              >
                Terms of Service
              </Link>
              <span className="text-white/15">·</span>
              <p className="text-white/30 text-[12px]">
                DCCF is a registered 501(c)(6) nonprofit organization.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
